import { useState } from "react";
import axios from "axios";
import { useForm } from "react-hook-form";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Reveal } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fieldClass =
  "w-full rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3.5 text-[15px] text-slate-100 placeholder:text-slate-500 outline-none transition-all duration-300 focus:border-teal/50 focus:bg-white/[0.05] focus:ring-2 focus:ring-teal/15";

export const Waitlist = () => {
  const [submitted, setSubmitted] = useState(false);
  const [alreadyRegistered, setAlreadyRegistered] = useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm();

  const onSubmit = async (data) => {
    try {
      const res = await axios.post(`${API}/waitlist`, {
        name: data.name,
        email: data.email,
        role: data.role || null,
      });
      setAlreadyRegistered(!!res.data?.already_registered);
      setSubmitted(true);
      toast.success(res.data?.already_registered ? "You're already on the list." : "You're on the list.");
    } catch (e) {
      toast.error("Something went wrong. Please try again.");
    }
  };

  return (
    <section id="waitlist" className="relative py-12 md:py-20 border-t border-white/[0.05]" data-testid="waitlist-section">
      <div className="absolute inset-0 sm-hero-glow pointer-events-none opacity-70" />
      <div className="relative mx-auto max-w-xl px-6 md:px-8 text-center">
        <Reveal>
          <p className="text-sm font-medium tracking-widest uppercase text-teal mb-5">Early access</p>
          <h2 className="font-heading text-4xl sm:text-5xl font-semibold tracking-tight text-slate-50 leading-[1.08]">
            Request early access
          </h2>
          <p className="mt-6 text-lg leading-relaxed text-slate-400">
            SalesMoat is currently in pilot. We're building a focused list of teams interested in the
            next phase — whether that's an expanded pilot or a controlled release.
          </p>
        </Reveal>

        <Reveal delay={0.12}>
          <div className="mt-12 rounded-3xl border border-white/[0.08] bg-navy-surface p-6 md:p-9 shadow-[0_20px_60px_rgba(0,0,0,0.3)] text-left">
            <AnimatePresence mode="wait">
              {submitted ? (
                <motion.div
                  key="success"
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="py-8 text-center"
                  data-testid="waitlist-success"
                >
                  <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-teal/15">
                    <Check className="h-7 w-7 text-teal" />
                  </div>
                  <p className="font-heading text-xl text-slate-100">
                    {alreadyRegistered ? "You're already on the list." : "Thanks — you're on the list."}
                  </p>
                  <p className="mt-2 text-[15px] text-slate-400">
                    We'll be in touch when the next phase opens.
                  </p>
                </motion.div>
              ) : (
                <motion.form
                  key="form"
                  onSubmit={handleSubmit(onSubmit)}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="space-y-4"
                  data-testid="waitlist-form"
                >
                  <div>
                    <label className="mb-2 block text-sm font-medium text-slate-300">Name</label>
                    <input
                      {...register("name", { required: "Name is required" })}
                      placeholder="Jane Doe"
                      className={fieldClass}
                      data-testid="waitlist-name-input"
                    />
                    {errors.name && (
                      <p className="mt-1.5 text-xs text-red-400" data-testid="waitlist-name-error">{errors.name.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="mb-2 block text-sm font-medium text-slate-300">Work email</label>
                    <input
                      {...register("email", {
                        required: "Work email is required",
                        pattern: { value: /^[^@\s]+@[^@\s]+\.[^@\s]+$/, message: "Enter a valid email" },
                      })}
                      placeholder="you@company.com"
                      className={fieldClass}
                      data-testid="waitlist-email-input"
                    />
                    {errors.email && (
                      <p className="mt-1.5 text-xs text-red-400" data-testid="waitlist-email-error">{errors.email.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="mb-2 block text-sm font-medium text-slate-300">
                      Role <span className="text-slate-500 font-normal">(optional)</span>
                    </label>
                    <input
                      {...register("role")}
                      placeholder="e.g. Head of Sales"
                      className={fieldClass}
                      data-testid="waitlist-role-input"
                    />
                  </div>

                  <p className="pt-1 text-center text-xs text-slate-500" data-testid="waitlist-consent">
                    By submitting, you agree to our{" "}
                    <Link to="/privacy" className="text-slate-400 underline underline-offset-2 hover:text-teal">Privacy Policy</Link>
                    {" "}and{" "}
                    <Link to="/terms" className="text-slate-400 underline underline-offset-2 hover:text-teal">Terms of Service</Link>.
                  </p>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    data-testid="waitlist-submit"
                    className="group mt-2 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-teal px-7 py-3.5 text-sm font-semibold text-navy transition-all duration-300 hover:bg-teal-dark hover:shadow-[0_8px_30px_rgba(20,184,166,0.25)] disabled:opacity-60"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" /> Joining…
                      </>
                    ) : (
                      "Join the waitlist"
                    )}
                  </button>

                  <p className="pt-1 text-center text-xs text-slate-500">
                    No spam. We'll only reach out about early access.
                  </p>
                </motion.form>
              )}
            </AnimatePresence>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
